import { Result } from 'antd';
import type { EffectivePermissions } from '@halo/shared';
import { lazy, type ReactNode } from 'react';
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import { AppLayout } from '@/layouts/AppLayout';
import { useCurrentUser } from '@/stores/session-store';

/*
 * Mỗi màn hình là MỘT gói mã riêng, tải khi người dùng mở nó lần đầu. Gom hết vào một gói thì lần mở
 * app đầu tiên phải tải mã của mọi màn hình (hơn 1 MB) — trên điện thoại dùng 4G là vài giây trắng
 * màn. Khung ứng dụng (`AppLayout`) và màn giữ chỗ thì tải ngay vì luôn cần. Khung chờ trong lúc tải
 * nằm ở `AppLayout` và `SeoLayout` (`Suspense` quanh `Outlet`).
 */
const DashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const WorklogPage = lazy(() =>
  import('@/features/worklog/WorklogPage').then((m) => ({ default: m.WorklogPage })),
);
const SeoLayout = lazy(() =>
  import('@/features/seo/SeoLayout').then((m) => ({ default: m.SeoLayout })),
);
const SeoOverviewPage = lazy(() =>
  import('@/features/seo/SeoOverviewPage').then((m) => ({ default: m.SeoOverviewPage })),
);
const SeoEntryPage = lazy(() =>
  import('@/features/seo/SeoEntryPage').then((m) => ({ default: m.SeoEntryPage })),
);
const SeoWeekPage = lazy(() =>
  import('@/features/seo/SeoWeekPage').then((m) => ({ default: m.SeoWeekPage })),
);
const SeoMonthPage = lazy(() =>
  import('@/features/seo/SeoMonthPage').then((m) => ({ default: m.SeoMonthPage })),
);
const MarketPage = lazy(() =>
  import('@/features/market/MarketPage').then((m) => ({ default: m.MarketPage })),
);
const ProductionPage = lazy(() =>
  import('@/features/production/ProductionPage').then((m) => ({ default: m.ProductionPage })),
);
const ProducerKpiPage = lazy(() =>
  import('@/features/production/ProductionPage').then((m) => ({ default: m.ProducerKpiPage })),
);
const ProducerSummaryPage = lazy(() =>
  import('@/features/production/ProductionPage').then((m) => ({
    default: m.ProducerSummaryPage,
  })),
);
const CompletedProjectsPage = lazy(() =>
  import('@/features/production/ProductionPage').then((m) => ({
    default: m.CompletedProjectsPage,
  })),
);
const RevenueEntryPage = lazy(() =>
  import('@/features/revenue/RevenueEntryPage').then((m) => ({ default: m.RevenueEntryPage })),
);
const RevenueReportPage = lazy(() =>
  import('@/features/revenue/RevenueReportPage').then((m) => ({ default: m.RevenueReportPage })),
);
const ChannelInfoPage = lazy(() =>
  import('@/features/channels/ChannelInfoPage').then((m) => ({ default: m.ChannelInfoPage })),
);
const MusicPage = lazy(() =>
  import('@/features/music/MusicPage').then((m) => ({ default: m.MusicPage })),
);
const CidPage = lazy(() => import('@/features/cid/CidPage').then((m) => ({ default: m.CidPage })));
const CompetitorsPage = lazy(() =>
  import('@/features/competitors/CompetitorsPage').then((m) => ({ default: m.CompetitorsPage })),
);
const OfficeResearchPage = lazy(() =>
  import('@/features/office-research/OfficeResearchPage').then((m) => ({
    default: m.OfficeResearchPage,
  })),
);
const VaultPage = lazy(() =>
  import('@/features/vault/VaultPage').then((m) => ({ default: m.VaultPage })),
);
const GpmPage = lazy(() => import('@/features/gpm/GpmPage').then((m) => ({ default: m.GpmPage })));
const ChannelOpsPage = lazy(() =>
  import('@/features/channel-ops/ChannelOpsPage').then((m) => ({ default: m.ChannelOpsPage })),
);
const VideoDownloadPage = lazy(() =>
  import('@/features/video-download/VideoDownloadPage').then((m) => ({
    default: m.VideoDownloadPage,
  })),
);
const VideoPublishPage = lazy(() =>
  import('@/features/video-publish/VideoPublishPage').then((m) => ({
    default: m.VideoPublishPage,
  })),
);
const OrgChartPage = lazy(() =>
  import('@/features/org-chart/OrgChartPage').then((m) => ({ default: m.OrgChartPage })),
);
const ProjectsPage = lazy(() =>
  import('@/features/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
);
const ThumbnailSummaryPage = lazy(() =>
  import('@/features/thumbnails/ThumbnailSummaryPage').then((m) => ({
    default: m.ThumbnailSummaryPage,
  })),
);
const ThumbnailLibraryPage = lazy(() =>
  import('@/features/thumbnails/ThumbnailLibraryPage').then((m) => ({
    default: m.ThumbnailLibraryPage,
  })),
);
const ThumbnailDownloadPage = lazy(() =>
  import('@/features/thumbnails/ThumbnailDownloadPage').then((m) => ({
    default: m.ThumbnailDownloadPage,
  })),
);
const ThumbnailMakerPage = lazy(() =>
  import('@/features/thumbnail-maker/ThumbnailMakerPage').then((m) => ({
    default: m.ThumbnailMakerPage,
  })),
);
const QuickThumbPage = lazy(() =>
  import('@/features/thumbnail-maker/QuickThumbPage').then((m) => ({
    default: m.QuickThumbPage,
  })),
);
const KpiPage = lazy(() => import('@/features/kpi/KpiPage').then((m) => ({ default: m.KpiPage })));
const AttendancePage = lazy(() =>
  import('@/features/attendance/AttendancePage').then((m) => ({ default: m.AttendancePage })),
);
const RequestsPage = lazy(() =>
  import('@/features/requests/RequestsPage').then((m) => ({ default: m.RequestsPage })),
);
const WorkReportsPage = lazy(() =>
  import('@/features/reports/WorkReportsPage').then((m) => ({ default: m.WorkReportsPage })),
);
const DailyGridPage = lazy(() =>
  import('@/features/reports/WorkReportsPage').then((m) => ({ default: m.DailyGridPage })),
);
const WeeklyMeetingPage = lazy(() =>
  import('@/features/meetings/WeeklyMeetingPage').then((m) => ({ default: m.WeeklyMeetingPage })),
);
const ProfilesPage = lazy(() =>
  import('@/features/profiles/ProfilesPage').then((m) => ({ default: m.ProfilesPage })),
);
const ActivityPage = lazy(() =>
  import('@/features/activity/ActivityPage').then((m) => ({ default: m.ActivityPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const AccountPage = lazy(() =>
  import('@/features/account/AccountPage').then((m) => ({ default: m.AccountPage })),
);

/** `/revenue/data?mode=…&month=…` (bản trước 22/09/2026) → chỗ mới của đúng chế độ đó. */
function LegacyRevenueDataRedirect() {
  const [params] = useSearchParams();
  const mode = params.get('mode');
  const month = params.get('month');
  const monthQuery = month ? `&month=${encodeURIComponent(month)}` : '';
  if (mode === 'grid') return <Navigate to={`/revenue/entry?view=month${monthQuery}`} replace />;
  const tab = mode === 'channels' ? 'entry' : 'summary';
  return <Navigate to={`/channels?tab=${tab}${monthQuery}`} replace />;
}

/** Chặn ở giao diện cho gọn; máy chủ vẫn là nơi chặn thật. */
function Guard({
  allow,
  reason = 'Mục này không dành cho tài khoản của bạn.',
  children,
}: {
  allow: (p: EffectivePermissions) => boolean;
  /** Lý do cụ thể, khi mục bị khoá vì một nguyên nhân người dùng cần biết. */
  reason?: string;
  children: ReactNode;
}) {
  const { permissions } = useCurrentUser();
  if (allow(permissions)) return children;
  return <Result status="403" title="Không đủ quyền" subTitle={reason} />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<DashboardPage />} />
        <Route path="seo" element={<SeoLayout />}>
          <Route index element={<SeoOverviewPage />} />
          <Route path="entry" element={<SeoEntryPage />} />
          <Route path="week" element={<SeoWeekPage />} />
          <Route path="month" element={<SeoMonthPage />} />
        </Route>
        <Route
          path="production"
          element={
            <Guard allow={(p) => p.canAccessProduction}>
              <ProductionPage />
            </Guard>
          }
        />
        <Route
          path="production/kpi"
          element={
            <Guard allow={(p) => p.canAccessProduction || p.canAccessProducerKpi}>
              <ProducerKpiPage />
            </Guard>
          }
        />
        <Route
          path="production/kpi/summary"
          element={
            <Guard allow={(p) => p.canAccessProduction || p.canAccessProducerKpi}>
              <ProducerSummaryPage />
            </Guard>
          }
        />
        <Route
          path="production/done"
          element={
            <Guard allow={(p) => p.canAccessProduction}>
              <CompletedProjectsPage />
            </Guard>
          }
        />
        {/* Ai cũng mở được: máy chủ chỉ trả về thư mục nhạc của nhóm người xem. */}
        <Route path="music" element={<MusicPage />} />
        <Route
          path="cid"
          element={
            <Guard allow={(p) => p.canAccessCid}>
              <CidPage />
            </Guard>
          }
        />
        {/* Không có Guard ở giao diện (như bản cũ): máy chủ tự giới hạn số liệu theo phạm vi của người gọi. */}
        <Route path="revenue/reports/:period" element={<RevenueReportPage />} />
        <Route
          path="revenue/entry"
          element={
            <Guard allow={(p) => p.canAccessRevenue}>
              <RevenueEntryPage />
            </Guard>
          }
        />
        {/* Không bọc Guard: ai cũng mở được — người không có quyền doanh thu chỉ thấy «Nhập thông tin kênh». */}
        <Route path="channels" element={<ChannelInfoPage />} />
        {/* Đường dẫn cũ của màn «Dữ liệu doanh thu» (nay tách thành «Thông tin kênh» và lưới cả tháng của
            «Nhập doanh thu») — link đã gửi cho nhau vẫn mở đúng chỗ. */}
        <Route path="revenue/data" element={<LegacyRevenueDataRedirect />} />
        <Route path="worklog" element={<WorklogPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="thumbnail-library" element={<ThumbnailLibraryPage />} />
        <Route path="thumbnail-summary" element={<ThumbnailSummaryPage />} />
        <Route path="thumbnail-download" element={<ThumbnailDownloadPage />} />
        <Route path="thumbnail-maker" element={<ThumbnailMakerPage />} />
        <Route path="thumbnail-quick" element={<QuickThumbPage />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="requests" element={<RequestsPage />} />
        <Route path="kpi" element={<KpiPage />} />
        <Route path="work-reports" element={<WorkReportsPage />} />
        <Route path="work-reports/days" element={<DailyGridPage />} />
        <Route path="meetings" element={<WeeklyMeetingPage />} />
        <Route
          path="video-publish"
          element={
            <Guard
              allow={(p) => p.canConfigure}
              reason="Hẹn giờ công khai video là sửa trạng thái video trên kênh công ty — chỉ Giám đốc làm được."
            >
              <VideoPublishPage />
            </Guard>
          }
        />
        {/* «Chỉ số kênh hệ thống» đã dời vào tab của Đăng video (11/10/2026) — giữ lối cũ, chuyển hướng sang đó. */}
        <Route
          path="channel-metrics"
          element={<Navigate to="/video-publish?tab=metrics" replace />}
        />
        <Route path="market" element={<MarketPage />} />
        <Route
          path="competitors"
          element={
            <Guard
              allow={(p) => p.canUseCompetitorResearch}
              reason="Bạn chưa được cấp quyền dùng mục này — nhờ Giám đốc tích tên ở «Nghiên cứu kênh đối thủ»."
            >
              <CompetitorsPage />
            </Guard>
          }
        />
        {/* Đường dẫn của mục «Nghiên cứu từ khóa» cũ (thay bằng Nghiên cứu kênh đối thủ ngày 22/09/2026). */}
        <Route path="office-research" element={<OfficeResearchPage />} />
        <Route path="keywords" element={<Navigate to="/competitors" replace />} />
        <Route
          path="vault"
          element={
            <Guard
              allow={(p) => p.canAccessVault}
              reason="Mục Hệ thống mail giữ mail, mật khẩu và mã 2FA của kênh — nhờ Giám đốc tích tên bạn ở Cài đặt."
            >
              <VaultPage />
            </Guard>
          }
        />
        <Route
          path="gpm"
          element={
            <Guard
              allow={(p) => p.canAccessVault}
              reason="Quản lý GPM dành cho người mở được Hệ thống mail — nhờ Giám đốc tích tên bạn ở Cài đặt."
            >
              <GpmPage />
            </Guard>
          }
        />
        <Route
          path="video-download"
          element={
            <Guard
              allow={(p) => p.canConfigure || p.canAccessVault}
              reason="Tải video dùng máy công ty qua cầu nối GPM — nhờ Giám đốc tích tên bạn ở Cài đặt (Hệ thống mail)."
            >
              <VideoDownloadPage />
            </Guard>
          }
        />
        <Route
          path="channel-ops"
          element={
            <Guard
              allow={(p) => p.canAccessVault}
              reason="Vận hành kênh dùng mail và profile GPM của kênh — nhờ Giám đốc tích tên bạn ở Cài đặt."
            >
              <ChannelOpsPage />
            </Guard>
          }
        />
        <Route path="org-chart" element={<OrgChartPage />} />
        <Route
          path="profiles"
          element={
            <Guard
              allow={(p) => p.canAccessProfiles}
              reason="Phòng của bạn thuộc chi nhánh khác — hồ sơ nhân sự do bên đó giữ."
            >
              <ProfilesPage />
            </Guard>
          }
        />
        <Route
          path="activity"
          element={
            <Guard
              allow={(p) => p.seesOthers}
              reason="Mục này dành cho người quản lý — bạn chỉ có phần việc của mình."
            >
              <ActivityPage />
            </Guard>
          }
        />
        <Route path="settings/:section?" element={<SettingsPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
